/**
 * Design Gallery Integration for Hat Lab
 * Handles design selection, localStorage management, and UI updates
 * 100% backwards compatible - all functions check for element existence
 */

(function() {
  'use strict';

  // Create namespace to avoid conflicts
  window.HATLAB_DESIGN = window.HATLAB_DESIGN || {};

  /**
   * LocalStorage key for selected design
   * Separate from hatlab_settings to avoid conflicts
   */
  const STORAGE_KEY = 'hatlab_selected_design';

  function escapeHtml(value) {
    const characters = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };

    return String(value || '').replace(/[&<>"']/g, function(character) {
      return characters[character];
    });
  }

  /**
   * Get currently selected design from localStorage
   * @returns {Object|null} Design data or null if none selected
   */
  HATLAB_DESIGN.getSelectedDesign = function() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.warn('Error reading selected design:', e);
      return null;
    }
  };

  /**
   * Save design selection to localStorage
   * @param {Object} designData - Design object with id, title, displayImage, etc.
   */
  HATLAB_DESIGN.selectDesign = function(designData) {
    try {
      const data = {
        id: designData.id,
        title: designData.title,
        description: designData.description || '',
        displayImage: designData.displayImage || designData.image, // For UI display
        thumbnail: designData.thumbnail || designData.displayImage || designData.image,
        previewImage: designData.previewImage || designData.image, // For future live preview
        selectedAt: Date.now(),
        selectedFrom: designData.selectedFrom || 'gallery'
      };
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      HATLAB_DESIGN.updateSelectedDesignBox();
      
      // Trigger custom event for other scripts to listen to
      document.dispatchEvent(new CustomEvent('hatlabDesignSelected', { detail: data }));
      
      return true;
    } catch (e) {
      console.warn('Could not save design selection:', e);
      return false;
    }
  };

  /**
   * Clear selected design from localStorage
   */
  HATLAB_DESIGN.clearSelectedDesign = function() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      
      // Remove design inputs from all forms
      const designInputs = document.querySelectorAll('input[data-design-input="true"]');
      designInputs.forEach(function(input) {
        input.remove();
      });
      
      HATLAB_DESIGN.updateSelectedDesignBox();
      
      // Trigger custom event
      document.dispatchEvent(new CustomEvent('hatlabDesignCleared'));
      
      return true;
    } catch (e) {
      console.warn('Could not clear design:', e);
      return false;
    }
  };

  /**
   * Sync selected design to cart forms, hide-when-design blocks, and the optional UI box.
   * Runs on pages without #SelectedDesignBox (e.g. bulk order) so data-hide-when-design still applies.
   */
  HATLAB_DESIGN.updateSelectedDesignBox = function() {
    const box = document.getElementById('SelectedDesignBox');
    const design = HATLAB_DESIGN.getSelectedDesign();

    if (!design) {
      if (box) {
        box.style.display = 'none';
      }

      const designInputs = document.querySelectorAll('input[data-design-input="true"]');
      designInputs.forEach(function(input) {
        input.remove();
      });

      HATLAB_DESIGN.updateFileUploadVisibility();
      return;
    }

    HATLAB_DESIGN.addDesignToForm(design);

    if (box) {
      const thumb = box.querySelector('.selected-design__thumb');
      if (thumb) {
        thumb.src = design.thumbnail;
        thumb.alt = design.title;
      }

      const name = box.querySelector('.selected-design__name');
      if (name) {
        name.textContent = design.title;
      }

      const tooltipImg = box.querySelector('.selected-design__tooltip img');
      if (tooltipImg) {
        tooltipImg.src = design.displayImage || design.thumbnail;
        tooltipImg.alt = design.title;
      }

      box.style.display = '';
    }

    HATLAB_DESIGN.updateFileUploadVisibility();
  };

  /**
   * Add design to product form as hidden input
   * @param {Object} design - Design object with title
   */
  HATLAB_DESIGN.addDesignToForm = function(design) {
    if (!design) return;
    
    // Find all product forms on the page
    const forms = document.querySelectorAll('form[action*="/cart/add"]');
    
    forms.forEach(function(form) {
      // Remove any existing design input
      const existingInput = form.querySelector('input[name="properties[Design]"]');
      if (existingInput) {
        existingInput.remove();
      }

      // Product Design Selector blocks already submit their own configured property
      // key, so do not add a second generic "Design" line item property for them.
      if (design.selectedFrom === 'product-block') {
        return;
      }
      
      // Add new hidden input with design name
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[Design]';
      input.value = design.title;
      input.setAttribute('data-design-input', 'true');
      form.appendChild(input);
    });
  };

  /**
   * Toggle file upload visibility based on design selection
   * Hides uploads that have data-hide-when-design="true" attribute
   */
  HATLAB_DESIGN.updateFileUploadVisibility = function() {
    const hasDesign = !!HATLAB_DESIGN.getSelectedDesign();
    const uploads = document.querySelectorAll('[data-hide-when-design="true"]');
    
    uploads.forEach(function(upload) {
      if (hasDesign) {
        upload.style.display = 'none';
        upload.setAttribute('aria-hidden', 'true');
        
        // Disable inputs to prevent submission
        const inputs = upload.querySelectorAll('input, textarea, select');
        inputs.forEach(function(input) {
          input.disabled = true;
          if (input.required) {
            input.setAttribute('data-was-required', 'true');
            input.required = false;
          }
        });
      } else {
        upload.style.display = '';
        upload.setAttribute('aria-hidden', 'false');
        
        // Re-enable inputs
        const inputs = upload.querySelectorAll('input, textarea, select');
        inputs.forEach(function(input) {
          input.disabled = false;
          if (input.getAttribute('data-was-required') === 'true') {
            input.required = true;
            input.removeAttribute('data-was-required');
          }
        });
      }
    });
  };

  /**
   * Open design dialog with design data
   * @param {Object} designData - Design object to display
   * @param {Object} options - Optional: context, inputId, actionLabel
   */
  HATLAB_DESIGN.openLightbox = function(designData, options) {
    const dialogComponent = document.getElementById('DesignDialog');
    if (!dialogComponent) return;
    
    const dialog = dialogComponent.querySelector('dialog');
    if (!dialog) return;
    
    options = options || {};
    
    // Store context for later use (determines if we redirect or not)
    dialog.setAttribute('data-context', options.context || 'gallery');
    if (options.inputId) {
      dialog.setAttribute('data-input-id', options.inputId);
    } else {
      dialog.removeAttribute('data-input-id');
    }
    
    // Update dialog content (use displayImage for UI, fallback to image for backwards compatibility)
    const img = dialog.querySelector('.design-dialog__image');
    const title = dialog.querySelector('.design-dialog__title');
    const description = dialog.querySelector('.design-dialog__description');
    const selectBtn = dialog.querySelector('.design-dialog__select-btn');
    
    if (img) {
      img.src = designData.displayImage || designData.image;
      img.alt = designData.title;
    }
    if (title) title.textContent = designData.title;
    if (description) {
      description.textContent = designData.description || '';
      description.style.display = designData.description ? '' : 'none';
    }
    
    // Store design data on select button
    if (selectBtn) {
      selectBtn.setAttribute('data-design-data', JSON.stringify(designData));
      
      // Update button text based on context
      if (options.context === 'product-page') {
        selectBtn.textContent = 'Select This Design';
      } else {
        selectBtn.textContent = options.actionLabel || HATLAB_DESIGN.getGalleryActionLabel();
      }
    }
    
    // Use the theme's dialog component method to show
    if (dialogComponent.showDialog) {
      dialogComponent.showDialog();
    } else {
      // dialog-component custom element not registered yet - wait for it
      customElements.whenDefined('dialog-component').then(function() {
        setTimeout(function() {
          if (dialogComponent.showDialog) dialogComponent.showDialog();
        }, 50);
      }).catch(function() {});
    }
  };

  /**
   * Close design dialog
   */
  HATLAB_DESIGN.closeLightbox = function() {
    const dialogComponent = document.getElementById('DesignDialog');
    if (!dialogComponent) return;
    
    // Use the theme's dialog component method to close
    if (dialogComponent.closeDialog) {
      dialogComponent.closeDialog();
    }
  };

  /**
   * Trap focus within an element (for modals)
   * @param {HTMLElement} element - Element to trap focus within
   */
  HATLAB_DESIGN.trapFocus = function(element) {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    
    // Handle tab key
    function handleTab(e) {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable.focus();
          e.preventDefault();
        }
      }
    }
    
    element.addEventListener('keydown', handleTab);
  };

  /**
   * Get design picker destination setting
   * @returns {String} 'hat-lab', 'bulk-order', 'product', or 'both'
   */
  HATLAB_DESIGN.getDestination = function() {
    return window.DESIGN_PICKER_DESTINATION || 'hat-lab';
  };

  /**
   * Get the default CTA label for gallery-originating design actions
   * @returns {String}
   */
  HATLAB_DESIGN.getGalleryActionLabel = function() {
    const destination = HATLAB_DESIGN.getDestination();

    if (destination === 'bulk-order') {
      return window.DESIGN_PICKER_BULK_ORDER_LABEL || 'Bring to Bulk Order';
    }

    if (destination === 'product') {
      return window.DESIGN_PICKER_PRODUCT_LABEL || 'Bring to Product';
    }

    if (destination === 'both') {
      return 'Choose destination';
    }

    return window.DESIGN_PICKER_HATLAB_LABEL || 'Bring to Hat Lab';
  };

  
  /**
   * Redirect to Hat Lab with design selected
   */
  HATLAB_DESIGN.redirectToHatlab = function(pageSlug) {
    const hatlabSlug = pageSlug || window.HATLAB_PAGE_SLUG || 'hat-lab';
    const hatlabUrl = '/pages/' + hatlabSlug + '?step=1';
    window.location.href = hatlabUrl;
  };

  /**
   * Redirect to Bulk Order with design selected
   * @param {number} [stepNum] - 1 or 2 (default 1)
   * @param {string} [pageSlug] - Explicit Bulk Order flow slug
   */
  HATLAB_DESIGN.redirectToBulkOrder = function(stepNum, pageSlug) {
    let activeBulkOrderSlug = '';
    const explicitBulkOrderSlug = pageSlug || '';

    if (explicitBulkOrderSlug) {
      activeBulkOrderSlug = explicitBulkOrderSlug;
      try {
        localStorage.setItem('bulk_order_active_page_slug', explicitBulkOrderSlug);
      } catch (e) {
        console.warn('Could not save active bulk order flow:', e);
      }
    } else {
      try {
        activeBulkOrderSlug = localStorage.getItem('bulk_order_active_page_slug') || '';
      } catch (e) {
        console.warn('Could not read active bulk order flow:', e);
      }
    }

    const bulkOrderSlug = activeBulkOrderSlug || window.BULK_ORDER_PAGE_SLUG || 'bulk-order';
    const step = stepNum === 2 ? 2 : 1;
    const bulkOrderUrl = '/pages/' + bulkOrderSlug + '?step=' + step;
    window.location.href = bulkOrderUrl;
  };

  /**
   * Redirect to the configured product with the selected design preserved
   */
  HATLAB_DESIGN.redirectToProduct = function() {
    const productUrl = window.DESIGN_PICKER_PRODUCT_URL || '';
    if (!productUrl) {
      console.warn('Design picker product destination is not configured.');
      HATLAB_DESIGN.redirectToHatlab();
      return;
    }

    window.location.href = productUrl;
  };

  /**
   * Handle redirect based on the configured design destination
   */
  HATLAB_DESIGN.handleDesignRedirect = function() {
    const destination = HATLAB_DESIGN.getDestination();

    if (destination === 'product') {
      HATLAB_DESIGN.redirectToProduct();
    } else if (destination === 'bulk-order') {
      HATLAB_DESIGN.showBulkOrderStepModal();
    } else if (destination === 'both') {
      HATLAB_DESIGN.showDestinationModal();
    } else {
      HATLAB_DESIGN.redirectToHatlab();
    }
  };

  /**
   * Customer-facing: choose bulk order step 1 (build) or 2 (customize) after selecting a design
   */
  HATLAB_DESIGN.showBulkOrderStepModal = function(pageSlug) {
    let modal = document.getElementById('BulkOrderStepModal');

    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'BulkOrderStepModal';
      modal.className = 'bulk-order-step-modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-labelledby', 'BulkOrderStepTitle');
      modal.setAttribute('aria-describedby', 'BulkOrderStepDesc');
      modal.innerHTML = `
        <div class="bulk-order-step-modal__backdrop"></div>
        <div class="bulk-order-step-modal__content">
          <h3 id="BulkOrderStepTitle" class="bulk-order-step-modal__title">Continue with bulk order</h3>
          <p id="BulkOrderStepDesc" class="bulk-order-step-modal__subtitle">Your design is saved. Items you added stay in your order until checkout. Where would you like to go next?</p>
          <div class="bulk-order-step-modal__actions">
            <button type="button" class="bulk-order-step-modal__btn bulk-order-step-modal__btn--primary" data-bulk-order-step="1">
              <span class="bulk-order-step-modal__btn-title">Add or change products</span>
              <span class="bulk-order-step-modal__btn-hint">Step 1 — build your order</span>
            </button>
            <button type="button" class="bulk-order-step-modal__btn bulk-order-step-modal__btn--outline" data-bulk-order-step="2">
              <span class="bulk-order-step-modal__btn-title">Customizations &amp; designs</span>
              <span class="bulk-order-step-modal__btn-hint">Step 2 — options, uploads, and design details</span>
            </button>
          </div>
          <button type="button" class="bulk-order-step-modal__close" aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      `;

      document.body.appendChild(modal);

      if (!document.getElementById('BulkOrderStepModalStyles')) {
        const styles = document.createElement('style');
        styles.id = 'BulkOrderStepModalStyles';
        styles.textContent = `
          .bulk-order-step-modal {
            position: fixed;
            inset: 0;
            z-index: 10000;
            display: none;
            align-items: center;
            justify-content: center;
            padding: 20px;
          }
          .bulk-order-step-modal.is-open {
            display: flex;
          }
          .bulk-order-step-modal__backdrop {
            position: absolute;
            inset: 0;
            background: rgba(0, 0, 0, 0.5);
          }
          .bulk-order-step-modal__content {
            position: relative;
            background: #fff;
            border-radius: 12px;
            padding: 2rem;
            max-width: 420px;
            width: 100%;
            text-align: center;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          }
          .bulk-order-step-modal__title {
            font-size: 1.25rem;
            font-weight: 700;
            margin: 0 0 0.5rem;
          }
          .bulk-order-step-modal__subtitle {
            font-size: 0.875rem;
            color: #6b7280;
            margin: 0 0 1.5rem;
            line-height: 1.5;
          }
          .bulk-order-step-modal__actions {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
          }
          .bulk-order-step-modal__btn {
            padding: 0.875rem 1.25rem;
            font-size: 1rem;
            font-weight: 600;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.2s ease;
            font-family: inherit;
            text-align: center;
          }
          .bulk-order-step-modal__btn-title {
            display: block;
          }
          .bulk-order-step-modal__btn-hint {
            display: block;
            margin-top: 0.35rem;
            font-size: 0.8125rem;
            font-weight: 400;
            opacity: 0.85;
          }
          .bulk-order-step-modal__btn--primary {
            background: #000;
            color: #fff;
            border: 2px solid #000;
          }
          .bulk-order-step-modal__btn--primary:hover {
            background: #fff;
            color: #000;
          }
          .bulk-order-step-modal__btn--primary .bulk-order-step-modal__btn-hint {
            color: rgba(255, 255, 255, 0.85);
          }
          .bulk-order-step-modal__btn--primary:hover .bulk-order-step-modal__btn-hint {
            color: #6b7280;
          }
          .bulk-order-step-modal__btn--outline {
            background: transparent;
            color: #000;
            border: 2px solid #000;
          }
          .bulk-order-step-modal__btn--outline .bulk-order-step-modal__btn-hint {
            color: #6b7280;
          }
          .bulk-order-step-modal__btn--outline:hover {
            background: #000;
            color: #fff;
          }
          .bulk-order-step-modal__btn--outline:hover .bulk-order-step-modal__btn-hint {
            color: rgba(255, 255, 255, 0.85);
          }
          .bulk-order-step-modal__btn:focus-visible {
            outline: 3px solid #0056b3;
            outline-offset: 2px;
          }
          .bulk-order-step-modal__close {
            position: absolute;
            top: 12px;
            right: 12px;
            background: none;
            border: none;
            padding: 4px;
            cursor: pointer;
            color: #6b7280;
          }
          .bulk-order-step-modal__close:focus-visible {
            outline: 3px solid #0056b3;
            outline-offset: 2px;
          }
          .bulk-order-step-modal__close:hover {
            color: #000;
          }
        `;
        document.head.appendChild(styles);
      }

      modal.querySelector('.bulk-order-step-modal__backdrop').addEventListener('click', function() {
        HATLAB_DESIGN.hideBulkOrderStepModal();
      });

      modal.querySelector('.bulk-order-step-modal__close').addEventListener('click', function() {
        HATLAB_DESIGN.hideBulkOrderStepModal();
      });

      modal.querySelectorAll('[data-bulk-order-step]').forEach(function(btn) {
        btn.addEventListener('click', function() {
          const step = parseInt(this.getAttribute('data-bulk-order-step'), 10);
          HATLAB_DESIGN.hideBulkOrderStepModal();
          HATLAB_DESIGN.redirectToBulkOrder(step, modal.dataset.pageSlug || '');
        });
      });
    }

    modal.dataset.pageSlug = pageSlug || '';
    modal.classList.add('is-open');
    const firstBtn = modal.querySelector('[data-bulk-order-step="1"]');
    if (firstBtn) {
      firstBtn.focus();
    }
  };

  /**
   * Hide bulk order step choice modal
   */
  HATLAB_DESIGN.hideBulkOrderStepModal = function() {
    const modal = document.getElementById('BulkOrderStepModal');
    if (modal) {
      modal.classList.remove('is-open');
    }
  };

  /**
   * Show destination choice modal
   */
  HATLAB_DESIGN.showDestinationModal = function() {
    // Check if modal already exists
    let modal = document.getElementById('DesignDestinationModal');
    
    if (!modal) {
      // Create modal
      const hatlabLabel = window.DESIGN_PICKER_HATLAB_LABEL || 'Bring to Hat Lab';
      const bulkOrderLabel = window.DESIGN_PICKER_BULK_ORDER_LABEL || 'Bring to Bulk Order';
      const productLabel = window.DESIGN_PICKER_PRODUCT_LABEL || 'Bring to Product';
      const includeHatlabs = window.DESIGN_PICKER_INCLUDE_HATLAB_IN_CHOICE !== false;
      const includeBulkOrders = window.DESIGN_PICKER_INCLUDE_BULK_ORDER_IN_CHOICE !== false;
      const extraHatlabs = Array.isArray(window.DESIGN_PICKER_EXTRA_HATLABS) ? window.DESIGN_PICKER_EXTRA_HATLABS : [];
      const extraBulkOrders = Array.isArray(window.DESIGN_PICKER_EXTRA_BULK_ORDERS) ? window.DESIGN_PICKER_EXTRA_BULK_ORDERS : [];
      const defaultHatlabSlug = extraHatlabs.length > 0 ? window.HATLAB_PAGE_SLUG || 'hat-lab' : '';
      const defaultBulkOrderSlug = extraBulkOrders.length > 0 ? window.BULK_ORDER_PAGE_SLUG || 'bulk-order' : '';
      const hatlabButtons = includeHatlabs
        ? `<button type="button" class="design-destination-modal__btn design-destination-modal__btn--hatlab" data-destination="hat-lab" data-page-slug="${escapeHtml(defaultHatlabSlug)}">${escapeHtml(hatlabLabel)}</button>` + extraHatlabs.map(function(destination) {
          return `<button type="button" class="design-destination-modal__btn design-destination-modal__btn--hatlab" data-destination="hat-lab" data-page-slug="${escapeHtml(destination.slug)}">${escapeHtml(destination.label)}</button>`;
        }).join('')
        : '';
      const bulkOrderButtons = includeBulkOrders
        ? `<button type="button" class="design-destination-modal__btn design-destination-modal__btn--bulk-order" data-destination="bulk-order" data-page-slug="${escapeHtml(defaultBulkOrderSlug)}">${escapeHtml(bulkOrderLabel)}</button>` + extraBulkOrders.map(function(destination) {
          return `<button type="button" class="design-destination-modal__btn design-destination-modal__btn--bulk-order" data-destination="bulk-order" data-page-slug="${escapeHtml(destination.slug)}">${escapeHtml(destination.label)}</button>`;
        }).join('')
        : '';
      const includeProduct = window.DESIGN_PICKER_INCLUDE_PRODUCT_IN_CHOICE === true && !!window.DESIGN_PICKER_PRODUCT_URL;
      const productButton = includeProduct
        ? `<button type="button" class="design-destination-modal__btn design-destination-modal__btn--product" data-destination="product">${escapeHtml(productLabel)}</button>`
        : '';

      if (!hatlabButtons && !bulkOrderButtons && !productButton) {
        console.warn('No design picker destinations are enabled for the choice modal.');
        return;
      }
      
      modal = document.createElement('div');
      modal.id = 'DesignDestinationModal';
      modal.className = 'design-destination-modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-labelledby', 'DesignDestinationTitle');
      modal.innerHTML = `
        <div class="design-destination-modal__backdrop"></div>
        <div class="design-destination-modal__content">
          <h3 id="DesignDestinationTitle" class="design-destination-modal__title">Where would you like to use this design?</h3>
          <p class="design-destination-modal__subtitle">Choose your destination to continue with your selected design.</p>
          <div class="design-destination-modal__actions">
            ${hatlabButtons}
            ${bulkOrderButtons}
            ${productButton}
          </div>
          <button type="button" class="design-destination-modal__close" aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      `;
      
      document.body.appendChild(modal);
      
      // Add modal styles if not already present
      if (!document.getElementById('DesignDestinationModalStyles')) {
        const styles = document.createElement('style');
        styles.id = 'DesignDestinationModalStyles';
        styles.textContent = `
          .design-destination-modal {
            position: fixed;
            inset: 0;
            z-index: 9999;
            display: none;
            align-items: center;
            justify-content: center;
            padding: 20px;
          }
          .design-destination-modal.is-open {
            display: flex;
          }
          .design-destination-modal__backdrop {
            position: absolute;
            inset: 0;
            background: rgba(0, 0, 0, 0.5);
          }
          .design-destination-modal__content {
            position: relative;
            background: #fff;
            border-radius: 12px;
            padding: 2rem;
            max-width: 400px;
            max-height: calc(100vh - 40px);
            width: 100%;
            overflow-y: auto;
            text-align: center;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          }
          .design-destination-modal__title {
            font-size: 1.25rem;
            font-weight: 700;
            margin: 0 0 0.5rem;
          }
          .design-destination-modal__subtitle {
            font-size: 0.875rem;
            color: #6b7280;
            margin: 0 0 1.5rem;
          }
          .design-destination-modal__actions {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
          }
          .design-destination-modal__btn {
            padding: 0.875rem 1.5rem;
            font-size: 1rem;
            font-weight: 600;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.2s ease;
            font-family: inherit;
          }
          .design-destination-modal__btn--hatlab {
            background: #000;
            color: #fff;
            border: 2px solid #000;
          }
          .design-destination-modal__btn--hatlab:hover {
            background: #fff;
            color: #000;
          }
          .design-destination-modal__btn--bulk-order,
          .design-destination-modal__btn--product {
            background: transparent;
            color: #000;
            border: 2px solid #000;
          }
          .design-destination-modal__btn--bulk-order:hover,
          .design-destination-modal__btn--product:hover {
            background: #000;
            color: #fff;
          }
          .design-destination-modal__close {
            position: absolute;
            top: 12px;
            right: 12px;
            background: none;
            border: none;
            padding: 4px;
            cursor: pointer;
            color: #6b7280;
          }
          .design-destination-modal__close:hover {
            color: #000;
          }
        `;
        document.head.appendChild(styles);
      }
      
      // Bind events
      modal.querySelector('.design-destination-modal__backdrop').addEventListener('click', function() {
        HATLAB_DESIGN.hideDestinationModal();
      });
      
      modal.querySelector('.design-destination-modal__close').addEventListener('click', function() {
        HATLAB_DESIGN.hideDestinationModal();
      });
      
      modal.querySelectorAll('.design-destination-modal__btn').forEach(function(btn) {
        btn.addEventListener('click', function() {
          const dest = this.getAttribute('data-destination');
          const pageSlug = this.getAttribute('data-page-slug') || '';
          HATLAB_DESIGN.hideDestinationModal();
          if (dest === 'bulk-order') {
            HATLAB_DESIGN.showBulkOrderStepModal(pageSlug);
          } else if (dest === 'product') {
            HATLAB_DESIGN.redirectToProduct();
          } else {
            HATLAB_DESIGN.redirectToHatlab(pageSlug);
          }
        });
      });
    }
    
    // Show modal
    modal.classList.add('is-open');
    const firstDestinationButton = modal.querySelector('.design-destination-modal__btn');
    if (firstDestinationButton) {
      firstDestinationButton.focus();
    }
  };

  /**
   * Hide destination choice modal
   */
  HATLAB_DESIGN.hideDestinationModal = function() {
    const modal = document.getElementById('DesignDestinationModal');
    if (modal) {
      modal.classList.remove('is-open');
    }
  };

  /**
   * Initialize design gallery interactions
   */
  HATLAB_DESIGN.initGallery = function() {
    // Design card hover interactions
    const designCards = document.querySelectorAll('.design-card');
    designCards.forEach(function(card) {
      const hatlabBtn = card.querySelector('.design-card__hatlab-btn');
      
      if (hatlabBtn) {
        hatlabBtn.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          
          const designData = JSON.parse(card.getAttribute('data-design'));
          designData.selectedFrom = 'gallery-hover';
          
          if (HATLAB_DESIGN.selectDesign(designData)) {
            HATLAB_DESIGN.handleDesignRedirect();
          }
        });
      }
      
      // Click card to open lightbox
      card.addEventListener('click', function() {
        const designData = JSON.parse(this.getAttribute('data-design'));
        HATLAB_DESIGN.openLightbox(designData, {
          actionLabel: hatlabBtn ? hatlabBtn.textContent.trim() : undefined
        });
      });
    });
    
    // Dialog handlers
    const dialogComponent = document.getElementById('DesignDialog');
    if (dialogComponent) {
      const dialog = dialogComponent.querySelector('dialog');
      const selectBtn = dialog ? dialog.querySelector('.design-dialog__select-btn') : null;
      
      if (selectBtn) {
        selectBtn.addEventListener('click', function() {
          const designData = JSON.parse(this.getAttribute('data-design-data'));
          const context = dialog.getAttribute('data-context') || 'gallery';
          const inputId = dialog.getAttribute('data-input-id');
          
          designData.selectedFrom = context === 'product-page' ? 'product-block' : 'gallery-lightbox';
          
          if (HATLAB_DESIGN.selectDesign(designData)) {
            HATLAB_DESIGN.closeLightbox();
            
            // If on product page with selector, check the input instead of redirecting
            if (context === 'product-page' && inputId) {
              const input = document.getElementById(inputId);
              if (input) {
                input.checked = true;
                // Trigger change event to update hidden inputs for checkbox mode
                input.dispatchEvent(new Event('change', { bubbles: true }));
              }
            } else {
              HATLAB_DESIGN.handleDesignRedirect();
            }
          }
        });
      }
    }
    
    // Remove design button handler
    const removeBtn = document.querySelector('.selected-design__remove');
    if (removeBtn) {
      removeBtn.addEventListener('click', function() {
        HATLAB_DESIGN.clearSelectedDesign();
      });
    }
    
    // Click thumbnail to open lightbox
    const thumbTrigger = document.querySelector('.selected-design__thumb-trigger');
    if (thumbTrigger) {
      thumbTrigger.addEventListener('click', function() {
        const design = HATLAB_DESIGN.getSelectedDesign();
        if (design) {
          HATLAB_DESIGN.openLightbox(design);
        }
      });
    }
  };

  /**
   * Initialize on page load
   */
  function init() {
    // Update selected design box if present
    if (typeof HATLAB_DESIGN.updateSelectedDesignBox === 'function') {
      HATLAB_DESIGN.updateSelectedDesignBox();
    }
    
    // Initialize gallery interactions if present
    HATLAB_DESIGN.initGallery();
  }

  // Expose backwards compatible global functions
  window.updateSelectedDesignBox = HATLAB_DESIGN.updateSelectedDesignBox;
  window.updateFileUploadVisibility = HATLAB_DESIGN.updateFileUploadVisibility;

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section load
  document.addEventListener('shopify:section:load', init);

  document.addEventListener('keydown', function(e) {
    if (e.key !== 'Escape') return;
    const bulk = document.getElementById('BulkOrderStepModal');
    if (bulk && bulk.classList.contains('is-open')) {
      HATLAB_DESIGN.hideBulkOrderStepModal();
      e.preventDefault();
      return;
    }
    const dest = document.getElementById('DesignDestinationModal');
    if (dest && dest.classList.contains('is-open')) {
      HATLAB_DESIGN.hideDestinationModal();
      e.preventDefault();
    }
  });
})();
